import {
  FormField,
  FormFieldList,
  SfInputFormField,
  SfSelectFormField,
} from 'ngx-smart-form';
import { IFormField } from 'ngx-smart-form/lib/smart-form/interfaces/i-form-field';
import { PraticaStats } from '../../pratica/models/pratica-stats';
import {
  DTOModulo,
  IKpi,
  KpiInfo,
  ModuloInfo,
  ModuloKey,
  ModuloOutcome,
} from './kpi-model';

export const moduloRecord: Record<keyof DTOModulo, PraticaStats> = {
  Modulo_1: PraticaStats.CREDIT_SCORE_CRIF,
  Modulo_2: PraticaStats.ANDAMENTALE,
  Modulo_3: PraticaStats.BILANCIO,
  Modulo_4: PraticaStats.GARANZIE,
  Modulo_5: PraticaStats.KYC,
  Modulo_6: PraticaStats.QUESTIONARIO_QUALITATIVO,
};

export const kpiModulesRecord: Record<PraticaStats, keyof DTOModulo> = {
  [PraticaStats.CREDIT_SCORE_CRIF]: 'Modulo_1',
  [PraticaStats.ANDAMENTALE]: 'Modulo_2',
  [PraticaStats.BILANCIO]: 'Modulo_3',
  [PraticaStats.GARANZIE]: 'Modulo_4',
  [PraticaStats.KYC]: 'Modulo_5',
  [PraticaStats.QUESTIONARIO_QUALITATIVO]: 'Modulo_6',
};

export class KpiFactory {
  static getScoreInfoObject(kpi: IKpi | null | undefined) {
    let sliceClassRecord: Record<
      ModuloOutcome,
      'error-bg' | 'warning-bg' | 'success-bg' | ''
    > = {
      KO: 'error-bg',
      ALERT: 'warning-bg',
      OK: 'success-bg',
      'N/A': '',
    };
    let scoreInfoObject: Partial<Record<PraticaStats, ModuloInfo>> = {};

    let kpiIsConsistent = !!kpi && !!kpi.Modulo_1;
    for (let key in moduloRecord) {
      let moduloKey = key as ModuloKey;
      let modulo = kpi?.[moduloKey]?.[0];
      let allModuli = kpi?.[moduloKey].slice(1);
      let position = Number(moduloKey.replace('Modulo_', ''));
      let section = moduloRecord[moduloKey];
      let outcome: ModuloOutcome = kpiIsConsistent
        ? kpi?.['Outcome_M' + position]
        : 'N/A';
      let outcomeCssClass = sliceClassRecord[outcome];
      let visibleModuli = allModuli?.filter(
        (el) => el.Visibilita === 'SI' || !el.Visibilita
      );
      let hasError =
        !!visibleModuli?.some((el) => el.Colore === 'ROSSO') &&
        outcomeCssClass !== 'error-bg';
      let hasWarning =
        !!visibleModuli?.some((el) => el.Colore === 'GIALLO') &&
        outcomeCssClass !== 'warning-bg';
      scoreInfoObject[section] = {
        position: position,
        outcomeCssClass: outcomeCssClass,
        score: modulo?.Valore || 'N/A',
        outcome: outcome,
        title: section,
        hasWarning: hasWarning,
        hasError: hasError,
      };
    }

    return scoreInfoObject;
  }

  static roundNumbers(num: string | undefined | null) {
    let scoreN = Number(num);
    let scoreS = isNaN(scoreN) ? 'N/A' : Math.round(scoreN).toString();
    return scoreS;
  }

  static getScoreInfo(kpi: IKpi | null | undefined) {
    let scoreInfoObject = KpiFactory.getScoreInfoObject(kpi);
    let scoreInfo = Object.values(scoreInfoObject);
    return scoreInfo.sort((a, b) => a.position! - b.position!);
  }

  static getModuloInfo(
    kpi: IKpi | undefined | null,
    section: PraticaStats,
    sliceFrom = 0
  ): (KpiInfo & { fieldList: FormFieldList })[] {
    let moduloKey = kpiModulesRecord[section];
    let moduloInfo = (kpi?.[moduloKey] || []).slice(1);
    return moduloInfo
      .map((el) => {
        let valore = Number(el.Valore);
        if (!isNaN(valore)) {
          el.numericValue = valore;
        }
        if (el.Descrizione === 'M2_TENSIONE_RISCHI_A_REVOCA') {
          console.log(el);
        }
        let decimals = el.Decimali
          ? Number(el.Decimali)
          : el.Formattazione
          ? 0
          : undefined;
        if (decimals && !isNaN(decimals)) {
          el.digitsInfo = `1.0-${decimals}`;
        }
        let fieldInfo = EDITABLE_FIELDS_RECORD[el.Descrizione];
        let field = fieldInfo
          ? getField(el.Descrizione, el.Valutazione, fieldInfo.options)
          : new SfInputFormField({
              formControlName: el.Descrizione,
              value: el.Valutazione,
              placeholder: 'Valutazione',
            });
        return {
          ...el,
          Visible: el.Visibilita === 'SI' || !el.Visibilita,
          fieldList: [[field]],
        };
      })
      .filter((el) => el.Visible);
  }

  static updateKpiModule(
    existingKpi: IKpi,
    formData: Record<string, string | null | undefined>,
    section: PraticaStats
  ) {
    let existingModuleData = KpiFactory.getModuloInfo(existingKpi, section);
    let updatedInfo = existingModuleData.map((el) => ({
      ...el,
      Valutazione: !el.Modificabile
        ? el.Valutazione
        : formData[el.Descrizione] || '',
    }));

    let moduleKey = kpiModulesRecord[section];
    let updatedKpiObject: Partial<IKpi> = {
      ...existingKpi,
      [moduleKey]: updatedInfo,
    };
    return updatedKpiObject;
  }
}

export const EDITABLE_FIELDS_RECORD: Record<
  string,
  (Partial<IFormField> & { options: string[] }) | undefined
> = {
  M2_ANOMALIE_PAGAMENTI_RATEALI: {
    options: [
      "PRESENZA RILEVANTE DI INSOLUTI NELL'ULTIMO TRIMESTRE",
      "PRESENZA NON RILEVANTE DI INSOLUTI NELL'ULTIMO TRIMESTRE",
      "PRESENZA DI INSOLUTI NEL PERIODO ANTECEDENTE ALL'ULTIMO TRIMESTRE",
      'PROFILO DEI PAGAMENTI REGOLARE',
      '00',
    ],
  },
  M2_CBS_EURISC: {
    options: ['ALTA', 'MEDIA', 'BASSA', 'NON CALCOLABILE'],
  },
  M2_ESCUSSIONE_GARANZIE: {
    options: ['PRATICA NON PROCESSABILE', 'PROFILO REGOLARE'],
  },
  M2_PAST_DUE_90GG_180GG: {
    options: ['PRATICA NON PROCESSABILE', 'PROFILO REGOLARE'],
  },
  M2_SEGNALAZIONI_INFRAMENSILI: {
    options: ['PRATICA NON PROCESSABILE', 'PROFILO REGOLARE'],
  },
  M2_SOFFERENZE: {
    options: ['PRATICA NON PROCESSABILE', 'PROFILO REGOLARE'],
  },
  M2_SOFFERENZE_PASSATE_A_PERDITA: {
    options: ['PRATICA NON PROCESSABILE', 'PROFILO REGOLARE'],
  },
  M3_DIO_ESPRESSO_IN_GG: {
    options: ['POSITIVO', 'ALERT', 'NEGATIVO'],
  },
  M3_DPO_ESPRESSO_IN_GG: {
    options: ['POSITIVO', 'ALERT', 'NEGATIVO'],
  },
  M3_DSO_ESPRESSO_IN_GG: {
    options: ['POSITIVO', 'ALERT', 'NEGATIVO'],
  },
  M3_PRODUTTIVITA_LORDA_LAVORO: {
    options: ['ALERT', 'POSITIVO'],
  },
  M3_REDDITIVITA_NETTA_AZIENDALE: {
    options: ['ALERT', 'POSITIVO'],
  },
  M3_REDDITIVITA_NETTA_TERRA: {
    options: ['ALERT', 'POSITIVO'],
  },
  M3_INTENSITA_FONDIARIA: {
    options: ['ALERT', 'POSITIVO'],
  },
  M3_INTENSITA_AGRARIA: {
    options: ['ALERT', 'POSITIVO'],
  },
  M3_GRADO_INTENSIVIZZAZIONE_TERRA: {
    options: ['ALERT', 'POSITIVO'],
  },
  M3_CARICO_BESTIAME: {
    options: ['ALERT', 'POSITIVO'],
  },
  M3_EQUILIBRIO_FINANZIARIO: {
    options: ['ALERT', 'POSITIVO'],
  },
  M3_INCIDENZA_ALTRI_RICAVI: {
    options: ['ALERT', 'POSITIVO'],
  },
  M3_INCIDENZA_CONTRIBUTI: {
    options: ['ALERT', 'POSITIVO'],
  },
  M4_CBS_EURISC_GARANTE: {
    options: ['ALTA', 'MEDIA', 'BASSA', 'NON CALCOLABILE'],
  },
  M4_CBS_EURISC_RAPPRESENTANTE_LEGALE: {
    options: ['ALTA', 'MEDIA', 'BASSA', 'NON CALCOLABILE'],
  },
  M5_CAMBIAMENTI_COMPAGINE_SOCIALE: {
    options: ['PRATICA PROCESSABILE', 'ALERT'],
  },
  M5_CAPITALE_VERSATO_DELIBERATO: undefined,
  M5_CODICE_ATECO: undefined,
  M5_CONTROLLI_LISTE_NEGATIVE: {
    options: ['PRATICA PROCESSABILE', 'PRATICA NON PROCESSABILE'],
  },
  M5_DATA_ULTIMO_CAMBIAMENTO_COMPAGINE_SOCIALE: undefined,
  M5_ETA_AMMINISTRATORE: undefined,
  M5_EVENTI_CONSERVATORIA: {
    options: ['PRATICA PROCESSABILE', 'ALERT'],
  },
};

const getField = (name: string, value: string, options: string[]) => {
  return new SfSelectFormField({
    formControlName: name,
    options: options.map((el) => ({ value: el, view: el })),
    placeholder: 'Valutazione',
    label: '',
    value: value,
  });
};
